import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  TemplateRef,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { OrgAdminSidebarComponent } from '../sidebar/org-admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import {
  TableComponent,
  TableColumn,
  PaginationConfig,
  ModalComponent,
} from '../../shared/components/primeng';
import {
  FilterPanelComponent,
  FilterField,
} from '../../shared/components/filter-panel/filter-panel.component';
import { ActionDropdownTriggerDirective } from '../../shared/directives/action-dropdown-trigger.directive';
import { ProviderPreviewMetadataComponent } from '../../shared/components/contribution-steps/preview-metadata/preview-metadata.component';
import { ManageFilesModalComponent } from '../../shared/components/manage-files-modal/manage-files-modal.component';
import {
  CatalogueApiService,
  SearchCriteria,
} from '@angular-workspace/services';
import { ToasterService } from '../../services/toaster.service';
import {
  TANUH_DATASET_DETAIL_KEYS,
  TANUH_DATASET_INPUT_KEYS,
  TANUH_DATASET_OUTPUT_PROFILE_KEYS,
  buildDatasetInputProfile,
  buildDatasetOutputProfile,
  buildDatasetDistributionsFromForm,
} from '../../shared/utils/tanuh-dataset-item-payload.util';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';

export interface ContributionData {
  assetName: string;
  assetType: string;
  permission: string;
  assetStatus: string;
  publishedOn: string;
  action: string;
  originalData?: any;
}

@Component({
  selector: 'app-org-admin-manage-contributions',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    TableComponent,
    FilterPanelComponent,
    ModalComponent,
    ProviderPreviewMetadataComponent,
    ActionDropdownTriggerDirective,
    ManageFilesModalComponent,
  ],
  templateUrl: './manage-contributions.component.html',
  styleUrl: './manage-contributions.component.scss',
})
export class OrgAdminManageContributionsComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('actionCell', { static: false })
  actionCellTemplate!: TemplateRef<any>;
  @ViewChild('permissionCell', { static: false })
  permissionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false })
  statusCellTemplate!: TemplateRef<any>;
  @ViewChild('publishedOnCell', { static: false })
  publishedOnCellTemplate!: TemplateRef<any>;

  /** Default sort when no query param or when Asset Type is selected (remove sort). */
  private readonly DEFAULT_SORT = 'itemCreatedAt:desc';

  /** Map table column key → API sort field. assetType → null means remove sort. */
  private readonly COLUMN_TO_API_SORT: Record<string, string | null> = {
    assetName: 'name',
    assetType: null,
    permission: 'accessPolicy',
    assetStatus: 'publishStatus',
    publishedOn: 'itemCreatedAt',
  };

  /** Map API sort field → table column key (for initial state from URL). */
  private readonly API_SORT_TO_COLUMN: Record<string, string> = {
    name: 'assetName',
    accessPolicy: 'permission',
    publishStatus: 'assetStatus',
    itemCreatedAt: 'publishedOn',
  };

  /** Current API sort string (e.g. 'itemCreatedAt:desc'). Used in loadContributions. */
  currentSortApi = this.DEFAULT_SORT;

  /** Initial sort for table header (from URL). */
  initialSortColumn: string | null = null;
  initialSortDirection: 'asc' | 'desc' = 'asc';

  private destroy$ = new Subject<void>();
  /** Skip next queryParams-triggered load when we just navigated from onSortChange. */
  private skipNextSortParamsLoad = false;

  // Modal state for Modify Asset (same as Access Request page)
  showModifyModal = false;
  currentModifyAsset: ContributionData | null = null;
  modifyAssetData: any = null;
  isLoadingAssetData = false;

  // Modal state for Manage Files (dataset/model versioning + file uploads)
  showManageFilesModal = false;
  manageFilesModalRow: ContributionData | null = null;

  constructor(
    private catalogueApiService: CatalogueApiService,
    private router: Router,
    private route: ActivatedRoute,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef,
    private keycloak: KeycloakService,
  ) {}

  columns: TableColumn[] = [
    { key: 'assetName', label: 'Asset Name', sortable: true },
    { key: 'assetType', label: 'Asset Type', sortable: false },
    { key: 'permission', label: 'Permission', sortable: true },
    { key: 'assetStatus', label: 'Asset Status', sortable: true },
    { key: 'publishedOn', label: 'Published On', sortable: true },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      fixed: 'right',
      width: '120px',
    },
  ];

  contributionsData: ContributionData[] = [];
  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };
  loading = false;
  emptyMessage = 'No contributions found';

  // Filter fields
  filterFields: FilterField[] = [
    {
      key: 'search',
      label: 'Search Assets',
      type: 'text',
      placeholder: 'Search assets...',
    },
  ];

  // Filter values
  searchTerm: string = '';

  // Search debounce
  private searchSubject = new Subject<string>();
  private searchSubscription: any;
  private isInitialLoad = true;

  openDropdownId: string | null = null;
  /** Position for fixed overlay (dropdown above table). */
  overlayPosition: { top: number; left: number; width: number } | null = null;

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakReady();
    // Sync sort from URL (and on browser back/forward). Skip load when we just navigated from onSortChange.
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (this.skipNextSortParamsLoad) {
          this.skipNextSortParamsLoad = false;
          return;
        }
        this.applySortFromParams(params['sort'] ?? null);
        this.loadContributions();
      });

    // Setup search debounce - only trigger loadContributions for search changes
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.searchTerm = searchTerm;
        this.pagination = { ...this.pagination, currentPage: 1 };
        this.loadContributions();
      });

    // Mark initial load as complete after a short delay to avoid filter panel init events
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 100);
  }

  /** Parse sort query param and set currentSortApi + table initial sort. */
  private applySortFromParams(sortParam: string | null): void {
    if (!sortParam || typeof sortParam !== 'string') {
      this.currentSortApi = this.DEFAULT_SORT;
      this.initialSortColumn = 'publishedOn';
      this.initialSortDirection = 'desc';
      return;
    }
    const [field, dir] = sortParam.split(':');
    const direction = dir === 'asc' || dir === 'desc' ? dir : 'asc';
    const apiField = (field && field.trim()) || null;
    if (!apiField || !this.API_SORT_TO_COLUMN[apiField]) {
      this.currentSortApi = this.DEFAULT_SORT;
      this.initialSortColumn = 'publishedOn';
      this.initialSortDirection = 'desc';
      return;
    }
    this.currentSortApi = `${apiField}:${direction}`;
    this.initialSortColumn = this.API_SORT_TO_COLUMN[apiField];
    this.initialSortDirection = direction;
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    const apiField = this.COLUMN_TO_API_SORT[event.column];
    this.skipNextSortParamsLoad = true; // avoid duplicate load when subscription fires after navigate
    if (apiField == null) {
      // Asset Type: remove sort from URL and use default
      this.currentSortApi = this.DEFAULT_SORT;
      this.initialSortColumn = 'publishedOn';
      this.initialSortDirection = 'desc';
      this.loadContributions();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { sort: null },
        queryParamsHandling: 'merge',
      });
      return;
    }
    const sortValue = `${apiField}:${event.direction}`;
    this.currentSortApi = sortValue;
    this.initialSortColumn = event.column;
    this.initialSortDirection = event.direction;
    this.loadContributions();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sort: sortValue },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.closeModifyModal();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) return;
      const maxWaitTime = 5000,
        checkInterval = 100,
        startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) return;
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    const actionColumn = this.columns.find((col) => col.key === 'action');
    if (actionColumn && this.actionCellTemplate) {
      actionColumn.cellTemplate = this.actionCellTemplate;
    }
    const permissionColumn = this.columns.find(
      (col) => col.key === 'permission',
    );
    if (permissionColumn && this.permissionCellTemplate) {
      permissionColumn.cellTemplate = this.permissionCellTemplate;
    }
    const statusColumn = this.columns.find((col) => col.key === 'assetStatus');
    if (statusColumn && this.statusCellTemplate) {
      statusColumn.cellTemplate = this.statusCellTemplate;
    }
    const publishedOnColumn = this.columns.find(
      (col) => col.key === 'publishedOn',
    );
    if (publishedOnColumn && this.publishedOnCellTemplate) {
      publishedOnColumn.cellTemplate = this.publishedOnCellTemplate;
    }
  }

  /** Map raw asset type to display label (e.g. adex:DataBank -> Dataset). */
  getAssetTypeDisplayLabel(rawType: string): string {
    if (!rawType || typeof rawType !== 'string') return '';
    const t = rawType.toLowerCase();
    if (t.includes('databank') || t.includes('database')) return 'Dataset';
    if (t.includes('aimodel') || t.includes('model')) return 'Model';
    if (t.includes('app') || t.includes('usecase')) return 'App';
    return rawType;
  }

  /** Permission display label: OPEN/PUBLIC -> Public, RESTRICTED -> Restricted. */
  getPermissionLabel(permission: string): string {
    if (!permission) return 'Public';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC') return 'Public';
    if (p === 'RESTRICTED') return 'Restricted';
    if (p === 'PRIVATE') return 'Private';
    return permission;
  }

  /** Badge wrapper class for permission column (icon + content, dataset collection style). */
  getPermissionBadgeClass(permission: string): string {
    if (!permission) return 'permission-badge';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC')
      return 'permission-badge permission-badge-public';
    if (p === 'RESTRICTED')
      return 'permission-badge permission-badge-restricted';
    if (p === 'PRIVATE') return 'permission-badge permission-badge-private';
    return 'permission-badge';
  }

  /** CSS class for permission chip. */
  getPermissionChipClass(permission: string): string {
    if (!permission) return 'contribution-chip contribution-chip--public';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC')
      return 'contribution-chip contribution-chip--public';
    if (p === 'RESTRICTED')
      return 'contribution-chip contribution-chip--restricted';
    if (p === 'PRIVATE') return 'contribution-chip contribution-chip--private';
    return 'contribution-chip';
  }

  private readonly IST_OPTIONS: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  /** Date part of Published On in IST (e.g. 06-Feb-2026). */
  getPublishedOnDatePart(dateValue: string): string {
    if (!dateValue) return '—';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return dateValue;
    const parts = new Intl.DateTimeFormat(
      'en-GB',
      this.IST_OPTIONS,
    ).formatToParts(d);
    const day = parts.find((p) => p.type === 'day')?.value ?? '00';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    const year = parts.find((p) => p.type === 'year')?.value ?? '';
    return `${day}-${month}-${year}`;
  }

  /** Time part of Published On in IST (e.g. 07:58). Styled grey, smaller font in template. */
  getPublishedOnTimePart(dateValue: string): string {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '';
    const parts = new Intl.DateTimeFormat(
      'en-GB',
      this.IST_OPTIONS,
    ).formatToParts(d);
    const hour = (parts.find((p) => p.type === 'hour')?.value ?? '00').padStart(
      2,
      '0',
    );
    const minute = (
      parts.find((p) => p.type === 'minute')?.value ?? '00'
    ).padStart(2, '0');
    return ` ${hour}:${minute}`;
  }

  /** Format Published On as DD-MMM-YYYY HH:MM in IST (e.g. 06-Feb-2026 07:58). */
  formatPublishedOn(dateValue: string): string {
    return (
      (
        this.getPublishedOnDatePart(dateValue) +
        this.getPublishedOnTimePart(dateValue)
      ).trim() || '—'
    );
  }

  /** Status display label: Pending, Granted, Rejected, etc. */
  getStatusLabel(status: string): string {
    if (!status) return '—';
    const s = (status + '').toUpperCase();
    if (s === 'ACTIVE' || s === 'PUBLISHED' || s === 'LIVE' || s === 'GRANTED')
      return 'Granted';
    if (s === 'PENDING') return 'Pending';
    if (s === 'DRAFT') return 'Draft';
    if (
      s === 'DECLINED' ||
      s === 'REJECTED' ||
      s === 'FAILED' ||
      s === 'DENIED'
    )
      return 'Rejected';
    return status;
  }

  /** CSS class for status chip (Pending = yellow, Granted = green, Rejected = red). */
  getStatusChipClass(status: string): string {
    if (!status) return 'contribution-chip contribution-chip--status-default';
    const s = (status + '').toUpperCase();
    if (s === 'ACTIVE' || s === 'PUBLISHED' || s === 'LIVE' || s === 'GRANTED')
      return 'contribution-chip contribution-chip--status-active';
    if (s === 'PENDING')
      return 'contribution-chip contribution-chip--status-pending';
    if (s === 'DRAFT')
      return 'contribution-chip contribution-chip--status-draft';
    if (
      s === 'DECLINED' ||
      s === 'REJECTED' ||
      s === 'FAILED' ||
      s === 'DENIED'
    )
      return 'contribution-chip contribution-chip--status-rejected';
    return 'contribution-chip contribution-chip--status-default';
  }

  /** Normalize API item to ContributionData; handles type as string or array (e.g. ["adex:AiModel"]). */
  private mapItemToContribution(item: any): ContributionData {
    const rawType = item.type ?? item.assetType ?? item.asset_type ?? '';
    const assetTypeRaw = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
    const assetTypeStr = typeof assetTypeRaw === 'string' ? assetTypeRaw : '';
    const permissionRaw =
      item.permission || item.accessPolicy || item.access_policy || 'PUBLIC';
    const statusRaw =
      item.publishStatus || item.publish_status || item.status || 'PENDING';
    return {
      assetName:
        item.title || item.name || item.assetName || item.asset_name || '',
      assetType: this.getAssetTypeDisplayLabel(assetTypeStr) || assetTypeStr,
      permission: permissionRaw,
      assetStatus: statusRaw,
      publishedOn:
        item.itemCreatedAt ||
        item.item_created_at ||
        item.createdAt ||
        item.created_at ||
        new Date().toISOString(),
      action: '',
      originalData: item,
    };
  }

  onFilterChange(filters: Record<string, any>): void {
    // Skip filter changes during initial load to prevent duplicate API calls
    if (this.isInitialLoad) {
      return;
    }

    // Handle search filter separately (debounced)
    if (filters['search'] !== undefined) {
      const newSearchTerm = filters['search'] || '';
      // Only update if search term actually changed
      if (this.searchTerm !== newSearchTerm) {
        this.searchSubject.next(newSearchTerm);
        // Don't load immediately for search - wait for debounce
      }
    }
  }

  onClearFilters(): void {
    this.searchTerm = '';
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadContributions();
  }

  /** Current user ID from Keycloak token (sub claim) for ownerUserId filter. */
  private getCurrentUserId(): string | null {
    try {
      const instance = this.keycloak.getKeycloakInstance();
      const parsed = (instance as any)?.tokenParsed;
      const sub = parsed?.sub ?? (instance as any)?.subject;
      return sub && typeof sub === 'string' ? sub : null;
    } catch {
      return null;
    }
  }

  async loadContributions(): Promise<void> {
    this.loading = true;
    try {
      const page = this.pagination.currentPage;
      const size = this.pagination.pageSize;

      const searchText = (this.searchTerm ?? '').toString().trim();
      const ownerUserId = this.getCurrentUserId();

      // Requirement:
      // - When user types search text: call `cat/search` via `searchAllAssets` and paginate results.
      // - When search text is cleared: fall back to the existing `getOrganisationAsset` call.
      let response: any;
      if (searchText) {
        const assetTypes = ['Dataset', 'Model', 'Usecase']; // maps to adex:DataBank/adex:AiModel/adex:Apps
        const additionalSearchCriteria: SearchCriteria[] = [];

        if (ownerUserId) {
          additionalSearchCriteria.push({
            searchType: 'term',
            field: 'ownerUserId',
            values: [ownerUserId],
          });
        }

        // `cat/search` expects sort with trailing ';' (matches your curl example).
        const searchSort = this.currentSortApi.endsWith(';')
          ? this.currentSortApi
          : `${this.currentSortApi};`;

        response = await this.catalogueApiService.searchAllAssets(
          searchText,
          assetTypes,
          [],
          page,
          size,
          searchSort,
          additionalSearchCriteria,
        );
      } else {
        // Existing API call when search is empty.
        const searchCriteria: SearchCriteria[] = [
          {
            searchType: 'term',
            field: 'type',
            values: ['adex:DataBank', 'adex:aiModel', 'adex:Apps'],
          },
        ];

        if (ownerUserId) {
          searchCriteria.push({
            searchType: 'term',
            field: 'ownerUserId',
            values: [ownerUserId],
          });
        }

        response = await this.catalogueApiService.getOrganisationAsset(
          page,
          size,
          this.currentSortApi,
          searchCriteria,
        );
      }

      // Map API response to ContributionData format
      let mappedData: ContributionData[] = [];
      let totalItems = 0;

      if (response) {
        // Handle different response structures
        if (response['result'] && Array.isArray(response['result'])) {
          mappedData = response['result'].map((item: any) =>
            this.mapItemToContribution(item),
          );
          totalItems =
            response['paginationInfo']?.totalCount ||
            response['totalCount'] ||
            response['total'] ||
            mappedData.length;
        } else if (response['data'] && Array.isArray(response['data'])) {
          mappedData = response['data'].map((item: any) =>
            this.mapItemToContribution(item),
          );
          totalItems =
            response['paginationInfo']?.totalCount ||
            response['totalCount'] ||
            response['total'] ||
            mappedData.length;
        } else if (Array.isArray(response)) {
          mappedData = response.map((item: any) =>
            this.mapItemToContribution(item),
          );
          totalItems = mappedData.length;
        }
      }

      this.contributionsData = mappedData;
      this.pagination = {
        ...this.pagination,
        totalItems: totalItems,
      };
    } catch (error) {
      console.error('Error loading contributions:', error);
      this.contributionsData = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
    } finally {
      this.loading = false;
    }
  }

  onPageChange(page: number): void {
    this.pagination = { ...this.pagination, currentPage: page };
    this.loadContributions();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: pageSize,
      currentPage: 1,
    };
    this.loadContributions();
  }

  toggleActionDropdown(event: Event, row: ContributionData): void {
    event.stopPropagation();
    event.preventDefault();
    const rowId = row.originalData?.id || JSON.stringify(row);
    if (this.openDropdownId === rowId) {
      this.openDropdownId = null;
      this.overlayPosition = null;
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.overlayPosition = {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
    this.openDropdownId = rowId;
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
    this.overlayPosition = null;
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): ContributionData | null {
    if (this.openDropdownId == null) return null;
    const found = this.contributionsData.find(
      (r) => (r?.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId,
    );
    return found ?? null;
  }

  /** Index in contributionsData of the row whose action dropdown is open; null when none. */
  get openDropdownRowIndex(): number | null {
    if (this.openDropdownId == null) return null;
    const idx = this.contributionsData.findIndex(
      (r) => (r?.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId,
    );
    return idx >= 0 ? idx : null;
  }

  isDropdownOpen(row: ContributionData): boolean {
    const rowId = row.originalData?.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }

  handleAction(action: string, row: ContributionData): void {
    this.closeActionDropdown();

    const original = row.originalData || {};
    const assetId =
      original.id ||
      original['assetId'] ||
      original['datasetId'] ||
      original['modelId'];

    if (action === 'view') {
      this.viewAsset(row);
    } else if (action === 'modify') {
      this.openModifyModal(row);
    } else if (action === 'files') {
      this.openManageFilesModal(row);
    } else if (action === 'delete') {
      // Remove Asset
      if (!assetId) {
        console.warn('Unable to remove asset: missing asset id', row);
        return;
      }
      this.catalogueApiService
        .deleteItem(assetId)
        .then(() => {
          this.toasterService.success("You've successfully removed the asset");
          this.loadContributions();
        })
        .catch((err) => {
          console.error('Error removing asset:', err);
        });
    }
  }

  /** View asset in new tab (opens asset detail page). */
  private viewAsset(row: ContributionData): void {
    const original = row.originalData || {};
    const assetId =
      original.id ||
      original['assetId'] ||
      original['datasetId'] ||
      original['modelId'];

    if (!assetId) {
      console.warn(
        'Unable to navigate to asset details: missing asset id',
        row,
      );
      this.toasterService.error('Unable to view asset: missing asset ID');
      return;
    }

    const typeLabel = (row.assetType || '').toLowerCase();
    let routeSegment: string | null = null;

    if (typeLabel === 'dataset') {
      routeSegment = 'dataset';
    } else if (typeLabel === 'model') {
      routeSegment = 'model';
    } else if (typeLabel === 'app') {
      routeSegment = 'usecase';
    } else {
      const rawType =
        original.type || original.assetType || original.asset_type || '';
      const rawTypeStr = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
      const rawTypeLower = (rawTypeStr + '').toLowerCase();

      if (
        rawTypeLower.includes('databank') ||
        rawTypeLower.includes('database')
      ) {
        routeSegment = 'dataset';
      } else if (
        rawTypeLower.includes('aimodel') ||
        rawTypeLower.includes('model')
      ) {
        routeSegment = 'model';
      } else if (
        rawTypeLower.includes('app') ||
        rawTypeLower.includes('usecase')
      ) {
        routeSegment = 'usecase';
      }
    }

    if (routeSegment) {
      // Open in new tab
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/${routeSegment}`, assetId]),
      );
      window.open(url, '_blank');
    } else {
      console.warn('Unknown asset type for navigation:', row.assetType, row);
      this.toasterService.error(
        'Unable to determine asset type for navigation',
      );
    }
  }

  /** Open Modify Asset modal and load asset data (same flow as Access Request page). */
  async openModifyModal(row: ContributionData): Promise<void> {
    const assetId =
      row.originalData?.id ||
      row.originalData?.assetId ||
      row.originalData?.datasetId ||
      row.originalData?.modelId;

    if (!assetId) {
      console.warn('Unable to modify asset: missing asset id', row);
      this.toasterService.error('Unable to modify asset: missing asset ID');
      return;
    }

    this.isLoadingAssetData = true;
    this.currentModifyAsset = row;

    try {
      const response = await this.catalogueApiService.getItemById(assetId);

      // GET /iudx/v2/cat/item?id=... wraps the item under `results` as a single object
      // (docs/openapi.yaml: successResponseForGet*Entity) — checked first since it's the
      // confirmed real backend shape; `.result`/`.data` are defensive fallbacks only.
      let assetData = response;
      if (
        response?.results &&
        typeof response.results === 'object' &&
        !Array.isArray(response.results)
      ) {
        assetData = response.results;
      } else if (
        Array.isArray(response?.results) &&
        response.results.length > 0
      ) {
        assetData = response.results[0];
      } else if (response?.result) {
        assetData = Array.isArray(response.result)
          ? response.result[0]
          : response.result;
      } else if (response?.data) {
        assetData = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
      } else if (Array.isArray(response) && response.length > 0) {
        assetData = response[0];
      }

      if (!assetData) {
        throw new Error('No asset data found in response');
      }

      this.modifyAssetData = this.mapAssetDataToPreviewFormat(
        assetData,
        row.assetType,
      );
      this.showModifyModal = true;
    } catch (error) {
      console.error('Error loading asset data for modification:', error);
      this.toasterService.error('Failed to load asset data. Please try again.');
      this.closeModifyModal();
    } finally {
      this.isLoadingAssetData = false;
      this.cdr.detectChanges();
    }
  }

  closeModifyModal(): void {
    this.showModifyModal = false;
    this.currentModifyAsset = null;
    this.modifyAssetData = null;
    this.isLoadingAssetData = false;
  }

  /** File uploads only apply to Dataset / Model assets (not Usecase/App). */
  isManageFilesAssetType(row: ContributionData | null): boolean {
    const typeLabel = (row?.assetType || '').toLowerCase();
    return typeLabel === 'dataset' || typeLabel === 'model';
  }

  getManageFilesItemId(row: ContributionData): string {
    const original = row.originalData || {};
    const id =
      original.id ||
      original['assetId'] ||
      original['datasetId'] ||
      original['modelId'];
    return id != null ? String(id).trim() : '';
  }

  openManageFilesModal(row: ContributionData): void {
    const id = this.getManageFilesItemId(row);
    if (!id) {
      this.toasterService.error('Unable to manage files: missing asset ID');
      return;
    }
    this.manageFilesModalRow = row;
    this.showManageFilesModal = true;
  }

  closeManageFilesModal(): void {
    this.showManageFilesModal = false;
    this.manageFilesModalRow = null;
  }

  getAssetTypeForModify(): 'dataset' | 'model' | null {
    if (!this.currentModifyAsset) return null;
    const typeLabel = (this.currentModifyAsset.assetType || '').toLowerCase();
    if (typeLabel === 'model') return 'model';
    if (typeLabel === 'dataset') return 'dataset';
    return null;
  }

  /** Ensure string meets preview-metadata min length (50 chars) so form is valid and Save can submit. */
  private ensureMinLength(str: string, min: number): string {
    const s = (str || '').trim();
    return s.length >= min ? s : s + ' '.repeat(Math.max(0, min - s.length));
  }

  /** Map API response to preview-metadata expected format (same as publish-assets). */
  private mapAssetDataToPreviewFormat(apiData: any, assetType: string): any {
    const isModel =
      assetType.toLowerCase().includes('model') ||
      (apiData.type &&
        Array.isArray(apiData.type) &&
        apiData.type.some((t: string) => t && (t + '').includes('AiModel'))) ||
      (apiData.type &&
        typeof apiData.type === 'string' &&
        (apiData.type + '').includes('AiModel'));

    const accessPolicy =
      apiData.accessPolicy || apiData.access_policy || 'OPEN';
    const permission =
      accessPolicy === 'OPEN'
        ? 'Open'
        : accessPolicy === 'RESTRICTED'
          ? 'Restricted'
          : 'Private';

    let yearRange = apiData.yearRange || apiData.year_range || null;
    if (typeof yearRange === 'string' && yearRange.includes('-')) {
      const parts = yearRange.split('-').map((p: string) => p.trim());
      if (parts.length === 2) {
        yearRange = {
          startYear: parseInt(parts[0], 10) || null,
          endYear: parseInt(parts[1], 10) || null,
        };
      }
    }

    const rawShort =
      apiData.shortDescription || apiData.short_description || '';
    const rawLong = apiData.description || apiData.longDescription || '';
    const shortDescription = this.ensureMinLength(rawShort, 50);
    const longDescription = this.ensureMinLength(rawLong, 50);

    return {
      // Spread the raw item first so fields this mapping doesn't know about (datasetMetrics,
      // modality, volume, output, primaryMetric/secondaryMetric, input*/output* profile fields,
      // etc. — the TANUH benchmark fields) still reach the preview-metadata form for prefill.
      // The explicit keys below are computed/normalized and intentionally override the raw ones.
      ...apiData,
      name: apiData.name || apiData.label || '',
      title: apiData.name || apiData.label || '',
      permission: permission,
      accessPolicy: accessPolicy,
      shortDescription,
      description: longDescription,
      longDescription,
      license: apiData.license || 'Open Government License, India',
      otherLicense: apiData.otherLicense || apiData.other_license || '',
      resourceServer: apiData.resourceServer || apiData.resource_server || '',
      uploadFrequency:
        apiData.uploadFrequency || apiData.upload_frequency || 'OneTime',
      yearRange: yearRange,
      geoCoverage: apiData.geoCoverage || apiData.geo_coverage || '',
      industry: apiData.industry || 'Agriculture',
      datasetType: isModel
        ? null
        : apiData.dataBankType || apiData.datasetType || 'Structured',
      fileFormat: apiData.fileFormat || apiData.file_format || 'CSV',
      tags: Array.isArray(apiData.tags)
        ? apiData.tags
        : apiData.tags
          ? [apiData.tags]
          : [],
      sampleDataUrl:
        apiData.mediaURL || apiData.media_url || apiData.sampleDataUrl || '',
      dataDescriptors: apiData.dataDescriptor || apiData.data_descriptor || [],
      type: apiData.type || [],
    };
  }

  async onModifyFormSubmit(formData: any): Promise<void> {
    if (!this.currentModifyAsset) return;

    const orig = this.currentModifyAsset.originalData || {};
    const assetId = orig.id || orig.assetId || orig.datasetId || orig.modelId;
    if (!assetId) {
      this.toasterService.error('Unable to update asset: missing asset ID');
      return;
    }

    try {
      const payload = this.buildUpdatePayload(formData, assetId);
      await this.catalogueApiService.updateItem(payload);

      this.toasterService.success('Asset metadata updated successfully');
      this.loadContributions();
      this.closeModifyModal();
    } catch (error) {
      console.error('Error updating asset:', error);
      this.toasterService.error('Failed to update asset. Please try again.');
    }
  }

  private buildUpdatePayload(formData: any, assetId: string): any {
    const isModel = this.getAssetTypeForModify() === 'model';
    const originalData = this.currentModifyAsset?.originalData || {};

    const rawType = originalData.type;
    const typeArray = Array.isArray(rawType)
      ? rawType
      : rawType
        ? [rawType]
        : [];

    const payload: any = {
      id: assetId,
      type:
        typeArray.length > 0
          ? typeArray
          : isModel
            ? ['adex:AiModel']
            : ['adex:DataBank'],
      name: formData.title || originalData.name,
      description: (
        formData.longDescription ||
        formData.description ||
        ''
      ).trim(),
      shortDescription: (formData.shortDescription || '').trim(),
      tags: Array.isArray(formData.tags) ? formData.tags : [],
      license: formData.license || '',
      industry: formData.industry || '',
      organizationId: originalData.organizationId,
      organizationType: originalData.organizationType,
      uploadedBy: originalData.uploadedBy,
      organization: originalData.organization,
      department: originalData.department,
    };

    if (isModel) {
      payload.modelType = formData.datasetType || originalData.modelType;
      payload.fileFormat = (formData.fileFormat || '').toString().toLowerCase();
      payload.fileSize = originalData.fileSize || '';
      payload.mediaURL = formData.sampleDataUrl || '';
    } else {
      payload.geoCoverage = formData.geoCoverage || '';
      payload.yearRange = this.formatYearRangeAsString(formData.yearRange);
      payload.resourceServer = this.buildResourceServerArray(
        formData.resourceServer || '',
      );
      payload.uploadFrequency = formData.uploadFrequency || '';
      payload.dataBankType = formData.datasetType || '';
      payload.fileFormat = (formData.fileFormat || '').toString().toLowerCase();
      payload.dataDescriptor = this.normalizeDataDescriptorToObject(
        formData.dataDescriptors,
      );
      if (formData.datasetMetrics) {
        payload.datasetMetrics = formData.datasetMetrics;
      }
      // A Croissant JSON uploaded in the modal replaces the stored Croissant document
      if (formData.croissant) {
        payload.croissant = JSON.stringify(formData.croissant);
      }
      // TANUH benchmark fields (modality/volume/output, primary/secondary metric, input/output
      // specs) are ALSO stored at the item's top level by the create flow (buildTanuhDatasetItemPayload),
      // duplicating what's nested under datasetMetrics — and the read side (extraction utils /
      // preview-metadata prefill) prefers the top-level copy. Without setting these here too, a
      // Modify Asset save only updates the nested copy, so edits appear to silently not "stick".
      for (const key of [
        ...TANUH_DATASET_DETAIL_KEYS,
        ...TANUH_DATASET_INPUT_KEYS,
        ...TANUH_DATASET_OUTPUT_PROFILE_KEYS,
      ]) {
        payload[key] = String(formData[key] ?? '').trim();
      }
      payload.datasetInput = buildDatasetInputProfile(formData);
      payload.datasetOutput = buildDatasetOutputProfile(formData);
      const distributions = buildDatasetDistributionsFromForm(formData);
      if (distributions.length > 0) {
        payload.distributions = distributions;
      }
    }

    const permission = formData.permission || 'Open';
    payload.accessPolicy =
      permission === 'Open'
        ? 'OPEN'
        : permission === 'Restricted'
          ? 'RESTRICTED'
          : 'PRIVATE';

    return payload;
  }

  /** yearRange for update API: string format "2020-2021" (API expects string). */
  private formatYearRangeAsString(yearRange: any): string | null {
    if (yearRange == null) return null;
    if (typeof yearRange === 'string') return yearRange.trim() || null;
    if (
      typeof yearRange === 'object' &&
      yearRange.startYear != null &&
      yearRange.endYear != null
    ) {
      return `${yearRange.startYear}-${yearRange.endYear}`;
    }
    return null;
  }

  private normalizeDataDescriptorToObject(
    dataDescriptors: any,
  ): Record<string, any> {
    if (
      dataDescriptors &&
      typeof dataDescriptors === 'object' &&
      !Array.isArray(dataDescriptors)
    ) {
      return dataDescriptors;
    }
    const list = Array.isArray(dataDescriptors) ? dataDescriptors : [];
    const obj: Record<string, any> = {};
    list.forEach((desc: any) => {
      if (desc && typeof desc === 'object' && desc.attributeName) {
        obj[desc.attributeName] = {
          parameterType: desc.parameterType || '',
          schemaType: desc.schemaType || '',
        };
      }
    });
    return obj;
  }

  private buildResourceServerArray(urlFromDropdown: string): any[] {
    const defaultId = 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1';
    const base = {
      id: defaultId,
      queryTypes: ['ATTR'],
      accessTypes: ['ATTR'],
      url: urlFromDropdown || '',
    };
    if (!urlFromDropdown || typeof urlFromDropdown !== 'string') {
      return [{ ...base, name: 'FILE', type: 'file' }];
    }
    const u = urlFromDropdown.toLowerCase();
    if (u.includes('ogc')) return [{ ...base, name: 'OGC', type: 'ogc' }];
    if (u.includes('rs.iudx') || u.includes('ngsi'))
      return [{ ...base, name: 'NGSI-LD', type: 'ngsi-ld' }];
    if (u.includes('rs-proxy') || u.includes('gateway'))
      return [{ ...base, name: 'GATEWAY', type: 'gateway' }];
    return [{ ...base, name: 'FILE', type: 'file' }];
  }

  onModifyFormCancel(): void {
    this.closeModifyModal();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (
      this.openDropdownId !== null &&
      !target.closest('.action-cell-wrapper') &&
      !target.closest('.action-dropdown-overlay')
    ) {
      this.closeActionDropdown();
    }
  }
}
